import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";

const COLOR_PRESETS = ["#16a34a", "#2563eb", "#f59e0b", "#db2777", "#7c3aed", "#dc2626", "#0891b2", "#059669", "#4f46e5"];

// Default gate Komisi Agen per jenis mitra bila toko belum mengatur (Reseller/Konsinyasi ON, Semi Reseller OFF)
const KOMISI_RULES_DEFAULT = { reseller: true, konsinyasi: true, semi_reseller: false };
const KOMISI_RULE_LABELS = { reseller: "Reseller", semi_reseller: "Semi Reseller", konsinyasi: "Konsinyasi" };

// int bila terisi, null bila kosong (tidak mengarang angka untuk tier yang belum diatur)
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v) || 0);

export function VoucherEditor({ storeId, onSaved }) {
  const [store, setStore] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const q = storeId ? { params: { store_id: storeId } } : {};

  const load = async () => {
    const [s, v] = await Promise.all([api.get("/admin/store", q), api.get("/admin/vouchers", q)]);
    setStore(s.data);
    setRows(v.data.map((x) => ({ ...x })));
  };
  useEffect(() => { load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addRow = () => setRows((r) => [...r, { id: `new-${Date.now()}`, _new: true, name: "", harga_beli: 0, harga_beli_agen: "", harga_beli_reseller: "", harga_beli_konsinyasi: "", harga_beli_semi_reseller: "", komisi_agen_reseller: "", komisi_agen_konsinyasi: "", komisi_agen_semi_reseller: "", harga_jual: 0, color: COLOR_PRESETS[r.length % COLOR_PRESETS.length] }]);
  const markDelete = (i) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, _delete: !row._delete } : row)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/store", { store_name: store.store_name, subtitle: store.subtitle, whatsapp_number: store.whatsapp_number, commission_rules: komisiRules }, q);
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // Harga Beli Reseller = nilai baru bila diisi, jika kosong pakai harga_beli lama (kompatibilitas)
        const resellerPrice = (row.harga_beli_reseller === "" || row.harga_beli_reseller == null)
          ? (Number(row.harga_beli) || 0)
          : (Number(row.harga_beli_reseller) || 0);
        const body = {
          name: row.name,
          harga_beli: resellerPrice,                     // legacy tetap = harga beli reseller (fallback)
          harga_beli_reseller: resellerPrice,
          harga_beli_agen: numOrNull(row.harga_beli_agen),
          harga_beli_konsinyasi: numOrNull(row.harga_beli_konsinyasi),
          harga_beli_semi_reseller: numOrNull(row.harga_beli_semi_reseller),
          komisi_agen_reseller: numOrNull(row.komisi_agen_reseller),
          komisi_agen_konsinyasi: numOrNull(row.komisi_agen_konsinyasi),
          komisi_agen_semi_reseller: numOrNull(row.komisi_agen_semi_reseller),
          harga_jual: Number(row.harga_jual) || 0,
          kategori: row.kategori === "ongkir" ? "ongkir" : "voucher",
          color: row.color, position: i,
        };
        if (row._new && !row._delete) { if (row.name.trim()) await api.post("/admin/vouchers", body, q); }
        else if (row._delete && !row._new) await api.delete(`/admin/vouchers/${row.id}`, q);
        else if (!row._new) await api.put(`/admin/vouchers/${row.id}`, body, q);
      }
      await load();
      onSaved?.();
      toast.success("Perubahan tersimpan. Semua pelanggan akan melihat harga baru.");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };

  if (!store) return <p className="empty-note">Memuat...</p>;
  const rights = store.access_rights || {};
  const canAdd = rights.tambah_voucher !== false;
  const canEdit = rights.edit !== false;
  const canDelete = rights.hapus !== false;
  const komisiRules = { ...KOMISI_RULES_DEFAULT, ...(store.commission_rules || {}) };
  const setKomisiRule = (k, v) => setStore({ ...store, commission_rules: { ...komisiRules, [k]: v } });

  return (
    <div data-testid="voucher-editor">
      <section className="settings-section">
        <h3 className="section-title">Info Toko</h3>
        <label className="field-label">Nama Toko</label>
        <input className="text-input" value={store.store_name || ""} onChange={(e) => setStore({ ...store, store_name: e.target.value })} data-testid="settings-store-name" />
        <label className="field-label">Subjudul</label>
        <input className="text-input" value={store.subtitle || ""} onChange={(e) => setStore({ ...store, subtitle: e.target.value })} data-testid="settings-subtitle" />
        <label className="field-label">Nomor WhatsApp (mis. 6281234567890)</label>
        <input className="text-input" value={store.whatsapp_number || ""} onChange={(e) => setStore({ ...store, whatsapp_number: e.target.value })} placeholder="6281234567890" data-testid="settings-whatsapp" />
      </section>

      <section className="settings-section" data-testid="komisi-rules-section">
        <h3 className="section-title">Komisi Agen per Jenis Mitra</h3>
        <p className="muted-sm" style={{ marginBottom: 8 }}>Atur apakah Agen memperoleh komisi saat melayani jenis mitra tertentu. Berlaku untuk transaksi BARU — histori transaksi lama tidak berubah. Komisi = Qty × Tarif Komisi (kolom Kom. di tabel voucher). Semi Reseller tanpa tarif komisi = Rp0 (atur tarifnya dulu, lalu aktifkan).</p>
        {["reseller", "semi_reseller", "konsinyasi"].map((k) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }} data-testid={`komisi-rule-${k}`}>
            <input type="checkbox" checked={!!komisiRules[k]} onChange={(e) => setKomisiRule(k, e.target.checked)} data-testid={`komisi-rule-${k}-toggle`} />
            <span>Komisi Agen untuk <strong>{KOMISI_RULE_LABELS[k]}</strong>: {komisiRules[k] ? "AKTIF" : "NONAKTIF"}</span>
          </label>
        ))}
      </section>

      <section className="settings-section">
        <h3 className="section-title">Daftar Voucher</h3>
        <p className="muted-sm" style={{ marginBottom: 8 }}>Harga Beli per jenis mitra (tier kosong = fallback Harga Beli Reseller). Komisi Agen TERPISAH dari harga — dipakai saat Agen melayani (kosong = standar: Reseller Rp100, Konsinyasi Rp150; Semi Reseller kosong = Rp0). Harga Jual dikunci Pusat.</p>
        <div className="voucher-editor-head">
          <span className="col-name">Nama</span>
          <span className="col-price">H. Agen</span>
          <span className="col-price">H. Reseller</span>
          <span className="col-price">H. Konsinyasi</span>
          <span className="col-price">H. Semi</span>
          <span className="col-price">Kom. Reseller</span>
          <span className="col-price">Kom. Konsinyasi</span>
          <span className="col-price">Kom. Semi</span>
          <span className="col-price">Harga Jual</span>
          <span className="col-color">Warna</span>
          <span className="col-act" />
        </div>
        {rows.map((row, i) => (
          <div key={row.id} className={`voucher-editor-row ${row._delete ? "row-deleted" : ""}`} data-testid={`editor-row-${i}`}>
            <input className="text-input col-name" value={row.name} placeholder="Nama voucher" onChange={(e) => setRow(i, "name", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-name-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Agen" value={row.harga_beli_agen ?? ""} onChange={(e) => setRow(i, "harga_beli_agen", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-beli-agen-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Reseller" value={row.harga_beli_reseller ?? row.harga_beli ?? ""} onChange={(e) => setRow(i, "harga_beli_reseller", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-beli-reseller-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Konsinyasi" value={row.harga_beli_konsinyasi ?? ""} onChange={(e) => setRow(i, "harga_beli_konsinyasi", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-beli-konsinyasi-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Semi" value={row.harga_beli_semi_reseller ?? ""} onChange={(e) => setRow(i, "harga_beli_semi_reseller", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-beli-semi-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Kom.Reseller" value={row.komisi_agen_reseller ?? ""} onChange={(e) => setRow(i, "komisi_agen_reseller", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-komisi-reseller-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Kom.Konsinyasi" value={row.komisi_agen_konsinyasi ?? ""} onChange={(e) => setRow(i, "komisi_agen_konsinyasi", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-komisi-konsinyasi-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Kom.Semi" value={row.komisi_agen_semi_reseller ?? ""} onChange={(e) => setRow(i, "komisi_agen_semi_reseller", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-komisi-semi-${i}`} />
            <input className="text-input col-price" type="number" placeholder="Jual" value={row.harga_jual} onChange={(e) => setRow(i, "harga_jual", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-jual-${i}`} />
            <label title="Ongkir: tanpa komisi Agen" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}><input type="checkbox" checked={row.kategori === "ongkir"} onChange={(e) => setRow(i, "kategori", e.target.checked ? "ongkir" : "voucher")} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-ongkir-${i}`} />Ongkir</label>
            <input className="color-input col-color" type="color" value={row.color} onChange={(e) => setRow(i, "color", e.target.value)} disabled={row._new ? !canAdd : !canEdit} data-testid={`editor-color-${i}`} />
            <button className={`icon-btn col-act ${row._delete ? "danger-active" : "danger"}`} onClick={() => markDelete(i)} disabled={!row._new && !canDelete} data-testid={`editor-delete-${i}`} title={!canDelete && !row._new ? "Dinonaktifkan superadmin" : row._delete ? "Batal hapus" : "Hapus"}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button className="add-item-btn" onClick={addRow} disabled={!canAdd} title={!canAdd ? "Dinonaktifkan superadmin" : ""} data-testid="editor-add-voucher"><Plus size={16} /> Tambah Voucher</button>
      </section>

      <div className="actions-right">
        <button className="btn-primary" onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />} Simpan Perubahan
        </button>
      </div>
    </div>
  );
}
